// Meta Pixel tracking utilities
// Non-blocking event tracking for e-commerce
import { logTrackingEvent } from '@/services/trackingService';
import { isTrackingEnabled } from '@/services/trackingSettingsService';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
  }
}

// Helper to safely call fbq with queueing and PocketBase logging
const safeFbq = async (method: string, event: string, params?: any) => {
  if (typeof window === 'undefined') return;
  
  // Check if tracking is enabled
  const trackingEnabled = await isTrackingEnabled();
  if (!trackingEnabled) {
    console.log(`Meta Pixel ${event} skipped: tracking disabled`);
    return;
  }
  
  try {
    if (window.fbq) {
      window.fbq(method, event, params);
      console.log(`Meta Pixel ${event} tracked:`, params);
    } else {
      // Queue the event if fbq is not ready yet
      window.fbq = window.fbq || function() {
        (window.fbq as any).callMethod ?
        (window.fbq as any).callMethod.apply(window.fbq, arguments) :
        (window.fbq as any).queue.push(arguments);
      };
      (window.fbq as any).queue = (window.fbq as any).queue || [];
      window.fbq(method, event, params);
    }
    
    // Log to PocketBase for admin tracking dashboard
    logTrackingEvent({
      event_type: event,
      event_id: params?.event_id || `${event}_${Date.now()}`,
      source: 'browser',
      metadata: params
    });
  } catch (error) {
    console.error(`Meta Pixel ${event} tracking error:`, error);
  }
};

/**
 * Track Add to Cart event
 * @param product - Product details
 * @param quantity - Quantity added
 */
/**
 * Track ViewContent event
 * @param product - Product details
 */
export const trackViewContent = (product: {
  id: string;
  title: string;
  price: number;
  category?: string;
}) => {
  safeFbq('track', 'ViewContent', {
    content_ids: [product.id],
    content_name: product.title,
    content_type: 'product',
    value: product.price,
    currency: 'USD',
    content_category: product.category || 'Educational Products'
  });
};

/**
 * Track Add to Cart event
 * @param product - Product details
 * @param quantity - Quantity added
 * @param extraMetadata - Optional extra metadata for admin dashboard
 */
export const trackAddToCart = (product: {
  id: string;
  title: string;
  price: number;
  category?: string;
}, quantity: number = 1, extraMetadata?: {
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  safeFbq('track', 'AddToCart', {
    content_ids: [product.id],
    content_name: extraMetadata?.tutor_name || product.title,
    content_type: 'product',
    value: product.price * quantity,
    currency: 'USD',
    content_category: product.category || 'Educational Products',
    num_items: quantity,
    // Additional metadata for admin dashboard
    tutor_name: extraMetadata?.tutor_name,
    student_name: extraMetadata?.student_name,
    student_email: extraMetadata?.student_email,
    subject: extraMetadata?.subject
  });
};

/**
 * Track Purchase event
 * @param items - Cart items
 * @param totalValue - Total purchase amount
 * @param tutorName - Optional tutor name for booking purchases (legacy support)
 * @param extraMetadata - Optional extra metadata for admin dashboard
 */
export const trackPurchase = (items: Array<{
  id: string;
  title: string;
  price: number;
  quantity: number;
}>, totalValue: number, tutorName?: string, extraMetadata?: {
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  const contentIds = items.map(item => item.id);
  const contents = items.map(item => ({
    id: item.id,
    quantity: item.quantity,
    item_price: item.price
  }));

  const effectiveTutorName = extraMetadata?.tutor_name || tutorName || items[0]?.title || 'Purchase';

  safeFbq('track', 'Purchase', {
    content_ids: contentIds,
    contents: contents,
    content_type: 'product',
    content_name: effectiveTutorName,
    value: totalValue,
    currency: 'USD',
    num_items: items.reduce((sum, item) => sum + item.quantity, 0),
    // Additional metadata for admin dashboard
    tutor_name: extraMetadata?.tutor_name || tutorName,
    student_name: extraMetadata?.student_name,
    student_email: extraMetadata?.student_email,
    subject: extraMetadata?.subject
  });
};

/**
 * Track Initiate Checkout event
 * @param items - Cart items
 * @param totalValue - Total checkout amount
 */
export const trackInitiateCheckout = (items: Array<{
  id: string;
  title: string;
  price: number;
  quantity: number;
}>, totalValue: number, extraMetadata?: {
  tutor_name?: string;
  student_name?: string;
  student_email?: string;
  subject?: string;
}) => {
  const contentIds = items.map(item => item.id);
  
  safeFbq('track', 'InitiateCheckout', {
    content_ids: contentIds,
    content_type: 'product',
    content_name: extraMetadata?.tutor_name || items[0]?.title || 'Checkout',
    value: totalValue,
    currency: 'USD',
    num_items: items.reduce((sum, item) => sum + item.quantity, 0),
    // Additional metadata for admin dashboard
    tutor_name: extraMetadata?.tutor_name,
    student_name: extraMetadata?.student_name,
    student_email: extraMetadata?.student_email,
    subject: extraMetadata?.subject
  });
};

/**
 * Track Search event
 * @param searchQuery - The search keyword/query
 */
export const trackSearch = (searchQuery: string) => {
  if (!searchQuery || searchQuery.trim().length === 0) return;
  
  safeFbq('track', 'Search', {
    search_string: searchQuery.trim()
  });
};

/**
 * Track Contact/Lead event
 * @param formType - Type of form (contact, quote, lead, etc.)
 */
export const trackContact = (formType: string = 'contact') => {
  safeFbq('track', 'Contact', {
    content_name: formType
  });
};

/**
 * Track Schedule event - for booking appointments/sessions
 * @param booking - Booking details
 */
export const trackSchedule = (booking: {
  id: string;
  subject: string;
  tutorName: string;
  date: string;
  price: number;
}) => {
  safeFbq('track', 'Schedule', {
    content_ids: [booking.id],
    content_name: booking.subject,
    content_type: 'appointment',
    value: booking.price,
    currency: 'EGP',
    content_category: 'Tutoring Session',
    custom_data: {
      tutor_name: booking.tutorName,
      booking_date: booking.date
    }
  });
};

/**
 * Track Lead event - for new customer acquisition
 * @param leadInfo - Lead details
 */
export const trackLead = (leadInfo: {
  contentName: string;
  value?: number;
  currency?: string;
}) => {
  safeFbq('track', 'Lead', {
    content_name: leadInfo.contentName,
    value: leadInfo.value || 0,
    currency: leadInfo.currency || 'EGP'
  });
};
